import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Warehouse, Location } from "@/data/mockWarehousesLocations";
import { getWarehouseTypes } from "./warehouse-types-settings-dialog";

interface WarehouseFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  warehouse: Warehouse | null;
  warehouses: Warehouse[];
  locations: Location[];
  onSave: (warehouse: Warehouse) => void;
}

export function WarehouseFormDialog({ open, onOpenChange, warehouse, warehouses, locations, onSave }: WarehouseFormDialogProps) {
  const [formData, setFormData] = useState<Partial<Warehouse>>({
    code: "",
    name: "",
    address: "",
    type: "None",
    active: true,
    defaultReceiveLocation: "",
    defaultPickLocation: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const warehouseTypes = useMemo(() => getWarehouseTypes(), []);

  // Filter locations for the current warehouse
  const warehouseLocations = useMemo(() => {
    if (!warehouse?.code || !locations) return [];
    return locations.filter(loc => loc.warehouseCode === warehouse.code);
  }, [locations, warehouse?.code]);

  useEffect(() => {
    if (warehouse) {
      setFormData(warehouse);
    } else {
      setFormData({
        code: "",
        name: "",
        address: "",
        type: "None",
        active: true,
        defaultReceiveLocation: "",
        defaultPickLocation: "",
      });
    }
    setErrors({});
  }, [warehouse, open]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.code?.trim()) {
      newErrors.code = "Code is required";
    } else if (formData.code.length > 10) {
      newErrors.code = "Code must be 10 characters or less";
    } else if (!warehouse && warehouses.some(w => w.code.toLowerCase() === formData.code.toLowerCase())) {
      newErrors.code = "This code already exists";
    }

    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.type) {
      newErrors.type = "Type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    onSave({
      ...formData,
      code: formData.code!,
      name: formData.name!,
      address: formData.address || "",
      type: formData.type as Warehouse["type"],
      active: formData.active !== false,
      defaultReceiveLocation: formData.defaultReceiveLocation,
      defaultPickLocation: formData.defaultPickLocation,
    } as Warehouse);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{warehouse ? "Edit Warehouse" : "Add Warehouse"}</DialogTitle>
          <DialogDescription>
            {warehouse ? "Update warehouse details" : "Create a new warehouse"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Code */}
          <div className="space-y-2">
            <Label htmlFor="code">
              Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="e.g., WH-A"
              maxLength={10}
              disabled={!!warehouse}
              className={errors.code ? "border-destructive" : ""}
            />
            {errors.code && <p className="text-sm text-destructive">{errors.code}</p>}
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Warehouse A"
              className={errors.name ? "border-destructive" : ""}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label htmlFor="type">
              Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData({ ...formData, type: value as Warehouse["type"] })}
            >
              <SelectTrigger className={errors.type ? "border-destructive" : ""}>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {warehouseTypes.map((type) => (
                  <SelectItem key={type.id} value={type.name}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.type && <p className="text-sm text-destructive">{errors.type}</p>}
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Full address including postcode"
              rows={3}
            />
          </div>

          {/* Default Locations - Only show for edit mode when locations exist */}
          {warehouse && warehouseLocations.length > 0 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="defaultReceiveLocation">Default Receive Location</Label>
                <Select
                  value={formData.defaultReceiveLocation || "none"}
                  onValueChange={(value) => setFormData({ ...formData, defaultReceiveLocation: value === 'none' ? undefined : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a location (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {warehouseLocations.map((location) => (
                      <SelectItem key={location.locationCode} value={location.locationCode}>
                        {location.locationCode} - {location.locationType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The location where goods are received by default
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="defaultPickLocation">Default Pick Location</Label>
                <Select
                  value={formData.defaultPickLocation || "none"}
                  onValueChange={(value) => setFormData({ ...formData, defaultPickLocation: value === 'none' ? undefined : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a location (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {warehouseLocations.map((location) => (
                      <SelectItem key={location.locationCode} value={location.locationCode}>
                        {location.locationCode} - {location.locationType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The location where goods are picked from by default
                </p>
              </div>
            </div>
          )}
          
          {/* Show message when creating new warehouse or no locations exist */}
          {(!warehouse || warehouseLocations.length === 0) && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                {!warehouse 
                  ? "Default receive and pick locations can be set after creating the warehouse and its locations."
                  : "No locations found for this warehouse. Create locations first to set default receive and pick locations."}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            {warehouse ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
